import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Button, Field, Input, InputGroup, Checkbox } from '@chakra-ui/react';
import { appointmentFeeSchema, TAppointmentFee } from './appointmentFee.validator';
import { TClinicDetailsProps } from '../../../templates/clinic/types/template.types';
import { signupStyles } from '@/components/features/registration/signup-templates/signup.styles';
import { LuPoundSterling } from 'react-icons/lu';

const AppointmentFeeForm = ({ next, details }: TClinicDetailsProps) => {
   const {
      register,
      handleSubmit,
      reset,
      formState: { errors },
   } = useForm<TAppointmentFee>({
      defaultValues: details,
      resolver: zodResolver(appointmentFeeSchema),
   });

   const [isChecked, setIsChecked] = React.useState<boolean>(false);

   const onSubmit = handleSubmit(async data => {
      next('availability', data);
   });

   const handleCheck = (checked: boolean | 'indeterminate') => {
      if (checked === 'indeterminate') return;
      setIsChecked(checked);
      register('isFreeAppointment', { value: checked });
   };

   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         <Stack gap="4" align="flex-start" minW="full">
            <Checkbox.Root checked={isChecked} onCheckedChange={({ checked }) => handleCheck(checked)}>
               <Checkbox.HiddenInput />
               <Checkbox.Control />
               <Checkbox.Label>Appointment for free</Checkbox.Label>
            </Checkbox.Root>
            {!isChecked && (
               <>
                  <Field.Root invalid={Boolean(errors.min_appointment_fee?.message)}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Minimum Appointment Fee
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <InputGroup startElement={<LuPoundSterling />}>
                        <Input
                           type="number"
                           placeholder="Minimum Appointment Fee"
                           variant="subtle"
                           h="10"
                           rounded="12px"
                           bg="#202020"
                           {...register('min_appointment_fee', { valueAsNumber: true })}
                        />
                     </InputGroup>
                     <Field.ErrorText>{errors.min_appointment_fee?.message}</Field.ErrorText>
                  </Field.Root>
                  <Field.Root invalid={Boolean(errors.max_appointment_fee?.message)}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Maximum Appointment Fee
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <InputGroup startElement={<LuPoundSterling />}>
                        <Input
                           type="number"
                           placeholder="Maximum Appointment Fee"
                           variant="subtle"
                           h="10"
                           rounded="12px"
                           bg="#202020"
                           {...register('max_appointment_fee', { valueAsNumber: true })}
                        />
                     </InputGroup>
                     <Field.ErrorText>{errors.max_appointment_fee?.message}</Field.ErrorText>
                  </Field.Root>
               </>
            )}

            <Box css={signupStyles.signup_btn_con}>
               <Button
                  type="reset"
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0">
                  Next
               </Button>
            </Box>
         </Stack>
      </Box>
   );
};

export default AppointmentFeeForm;
